import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { allowedTransitions, type Transition } from '@monrdv/shared';
import { db, type LocalPlace, type LocalStop } from '../lib/localdb.js';
import { subscribe } from '../lib/outbox.js';
import { heldCapabilities } from '../lib/api.js';
import { transition } from '../lib/actions.js';
import { addressLines, amount, mapHref, shortTime, text, windowLabel } from '../lib/format.js';
import { requirementsFor, summarise, type Checklist, type RequirementStatus } from '../lib/stop.js';
import { BigButton, CheckRow, Sheet } from '../components/Controls.js';
import { StateBadge } from './Today.js';
import { AtTheDoor } from './AtTheDoor.js';
import { WorkPanel } from './Work.js';
import { FinishPanel } from './Finish.js';
import { CannotDoPanel } from './CannotDo.js';

/**
 * The stop.
 *
 * Opened from Today, and the only screen with a state machine:
 *
 *     scheduled/confirmed → en route → arrived → working → done
 *                           ↻ delayed                ↘ stopped ↗
 *                              ↘────── cannot do ───────────↗
 *
 * Two things govern the whole screen.
 *
 * **The primary action is always one control, always in the same place, always reachable with
 * a thumb.** A technician holding a light in the other hand should never have to find the
 * button; the state decides what it says.
 *
 * **Nothing renders that the account cannot use.** A disabled button is a question the
 * technician has to ask somebody about, so a control the capabilities do not allow is absent
 * rather than greyed out.
 */

type Panel = 'door' | 'work' | 'finish' | 'cannot';

export function StopScreen() {
  const { stopId } = useParams({ strict: false }) as { stopId: string };
  const search = useSearch({ strict: false }) as { panel?: Panel };
  const navigate = useNavigate();

  const [stop, setStop] = useState<LocalStop | null | undefined>(undefined);
  const [place, setPlace] = useState<LocalPlace | null>(null);
  const [requirements, setRequirements] = useState<RequirementStatus[]>([]);
  const [capabilities, setCapabilities] = useState<Set<string>>(new Set());
  const [delayOpen, setDelayOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const panel: Panel = search.panel ?? 'door';

  const load = useCallback(async () => {
    const database = await db();
    const found = (await database.get('stops', stopId)) ?? null;
    setStop(found);
    if (found?.address) setPlace((await database.get('places', found.address.placeKey)) ?? null);
    if (found) setRequirements(await requirementsFor(found));
    setCapabilities(await heldCapabilities());
  }, [stopId]);

  useEffect(() => {
    void load();
    return subscribe(() => void load());
  }, [load]);

  const checklist: Checklist | null = useMemo(
    () => (stop ? summarise(requirements, stop) : null),
    [requirements, stop],
  );

  if (stop === undefined) return <div className="loading" aria-busy="true" />;
  if (stop === null) {
    return (
      <div className="empty">
        <p>Cet arrêt n’est plus dans la fenêtre synchronisée.</p>
        <Link to="/" className="empty__action">
          Retour à la journée
        </Link>
      </div>
    );
  }

  const { primary, secondary } = addressLines(stop.address);
  const map = mapHref(stop.address);
  const phone = stop.phones[0];
  const allowed = allowedTransitions(stop.state);

  const move = async (which: Transition, extra?: Parameters<typeof transition>[2]) => {
    setBusy(true);
    try {
      await transition(stop.id, which, extra);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const show = (next: Panel) => navigate({ to: '/arret/$stopId', params: { stopId }, search: { panel: next } });

  return (
    <div className="stopscreen">
      <header className="stopscreen__head">
        <Link to="/" className="back" aria-label="Retour">
          ‹
        </Link>
        <div className="stopscreen__title">
          <h1>{stop.customer.name}</h1>
          <p className="tnum">
            {windowLabel(stop)}
            {stop.number ? ` · ${stop.number}` : ''}
          </p>
        </div>
        <StateBadge state={stop.state} etaAt={stop.etaAt} />
      </header>

      <section className="stopscreen__where">
        <p className="stopscreen__address">
          {primary}
          <span>{secondary}</span>
        </p>
        <div className="stopscreen__links">
          {map && (
            <a className="ghost" href={map}>
              Trajet
            </a>
          )}
          {phone && (
            <a className="ghost" href={`tel:${phone.normalized}`}>
              Appeler
            </a>
          )}
        </div>
        {stop.address?.note && (
          <p className="stopscreen__access">
            <span className="label">Accès</span>
            {stop.address.note}
          </p>
        )}
        {stop.services.length > 0 && (
          <p className="stopscreen__services">{stop.services.map((service) => text(service.name)).join(' · ')}</p>
        )}
      </section>

      {/*
        Progress as work remaining, never as a percentage.
        "60%" invites the question "60% of what"; each of these is either done or it is not,
        and every one of them is a thing somebody can act on.
      */}
      {checklist && (
        <ul className="checklist" aria-label="Ce qu’il reste à faire">
          {checklist.captures.total > 0 && (
            <CheckRow done={checklist.captures.done === checklist.captures.total}>
              Photos {checklist.captures.done} de {checklist.captures.total}
            </CheckRow>
          )}
          {checklist.readings.total > 0 && (
            <CheckRow done={checklist.readings.done === checklist.readings.total} warn={checklist.outOfSpec}>
              Relevés {checklist.readings.done} de {checklist.readings.total}
              {checklist.outOfSpec ? ' · hors norme' : ''}
            </CheckRow>
          )}
          {checklist.signatures.total > 0 && (
            <CheckRow done={checklist.signatures.done === checklist.signatures.total}>
              Signatures {checklist.signatures.done} de {checklist.signatures.total}
            </CheckRow>
          )}
          <CheckRow done={checklist.parts > 0}>Pièces {checklist.parts}</CheckRow>
          <CheckRow done={Boolean(stop.arrivedAt)}>
            {stop.arrivedAt ? `Sur place depuis ${shortTime(stop.arrivedAt)}` : 'Pas encore arrivé'}
          </CheckRow>
          {Number(stop.job.balance) > 0 && (
            <CheckRow done={false} warn>
              Solde dû {amount(stop.job.balance)}
            </CheckRow>
          )}
        </ul>
      )}

      <nav className="tabs" aria-label="Sections">
        <button type="button" className={panel === 'door' ? 'on' : ''} onClick={() => show('door')}>
          À la porte
        </button>
        <button type="button" className={panel === 'work' ? 'on' : ''} onClick={() => show('work')}>
          Travaux
        </button>
        <button type="button" className={panel === 'finish' ? 'on' : ''} onClick={() => show('finish')}>
          Terminer
        </button>
      </nav>

      <div className="stopscreen__panel">
        {panel === 'door' && <AtTheDoor stop={stop} place={place} capabilities={capabilities} />}
        {panel === 'work' && <WorkPanel stop={stop} place={place} requirements={requirements} onChanged={load} />}
        {panel === 'finish' && (
          <FinishPanel stop={stop} checklist={checklist} capabilities={capabilities} onDone={() => navigate({ to: '/' })} />
        )}
        {panel === 'cannot' && <CannotDoPanel stop={stop} onDone={() => navigate({ to: '/' })} />}
      </div>

      {/*
        The primary action.
        One control, always at the bottom, always saying what happens next. `allowedTransitions`
        decides what exists — a control the state cannot accept is absent, not disabled.
      */}
      <footer className="stopscreen__act">
        {allowed.includes('en_route') && (
          <BigButton onClick={() => void move('en_route')} busy={busy} hint="L’office le voit tout de suite">
            En route
          </BigButton>
        )}
        {allowed.includes('delay') && (
          <BigButton tone="warm" onClick={() => setDelayOpen(true)} busy={busy}>
            En retard
          </BigButton>
        )}
        {allowed.includes('arrive') && (
          <BigButton onClick={() => void move('arrive')} busy={busy} hint="Position enregistrée">
            Arrivé
          </BigButton>
        )}
        {allowed.includes('complete') && (
          <BigButton tone="positive" onClick={() => show('finish')} busy={busy}>
            Terminer
          </BigButton>
        )}
        {allowed.includes('cannot_do') && (
          <BigButton tone="quiet" onClick={() => show('cannot')} busy={busy}>
            Impossible
          </BigButton>
        )}
        {allowed.includes('undo') && allowed.length === 1 && (
          <BigButton tone="quiet" onClick={() => void move('undo')} busy={busy} hint="Revenir d’une étape">
            Annuler la dernière action
          </BigButton>
        )}
      </footer>

      <DelaySheet
        open={delayOpen}
        onClose={() => setDelayOpen(false)}
        onPick={(etaAt) => {
          setDelayOpen(false);
          void move('delay', { etaAt });
        }}
      />
    </div>
  );
}

/**
 * *En retard*, with a new arrival time.
 *
 * Traffic is daily; abandoning a stop is rare. Offered as four taps rather than a time picker
 * because the answer is almost always "another twenty minutes", and the office needs it now
 * rather than accurately.
 */
function DelaySheet({ open, onClose, onPick }: { open: boolean; onClose: () => void; onPick: (etaAt: string) => void }) {
  const options = [15, 30, 45, 60];
  return (
    <Sheet open={open} title="Nouvelle heure d’arrivée" onClose={onClose}>
      <div className="delay">
        {options.map((mins) => {
          const at = new Date(Date.now() + mins * 60_000);
          return (
            <BigButton key={mins} tone="warm" onClick={() => onPick(at.toISOString())}>
              +{mins} min <span className="tnum">{shortTime(at)}</span>
            </BigButton>
          );
        })}
      </div>
      <p className="hint">L’office le voit tout de suite et peut prévenir le client.</p>
    </Sheet>
  );
}
